// src/views/StoreAnalytics.js
import React, { useState, useEffect } from "react";
import {
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CRow,
  CWidgetStatsA,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CBadge,
  CFormSelect,
  CSpinner,
  CFormLabel,
} from "@coreui/react";
import { CChartBar, CChartDoughnut, CChartLine, CChartPie, CChartPolarArea, CChartRadar } from "@coreui/react-chartjs";
import CIcon from "@coreui/icons-react";
import { cibEventStore, cilChartPie, cilMap, cilCalendar } from "@coreui/icons";
import {
  getSummary,
  getVisitTrends,
  getGeographicDistribution,
  getSalesPerformance,
  getMapDistribution,
} from "src/services/Analytics";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useNavigate } from "react-router-dom";
import SalesPicker from "src/components/SalesPicker";
import ls from "local-storage";

// Fix for default marker icons (required for React-Leaflet)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png",
});
const StoreAnalytics = () => {
  const [summary, setSummary] = useState([]);
  const [visitTrends, setVisitTrends] = useState([]);
  const [mapDistribution, setMapDistribution] = useState([]);
  const [geoDistribution, setGeoDistribution] = useState([]);
  const [salesPerformance, setSalesPerformance] = useState([]);
  const [timeRange, setTimeRange] = useState("monthly");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const center = [-6.91, 107.61]; // Latitude, Longitude of Bandung

  const getFilterParams = () => {
    const smd = ls.get("smd");
    const prov = ls.get("prov");
    const city = ls.get("city");
    
    return {
      // dateFrom: Moment(dateRange[0].startDate).format("YYYY-MM-DD"),
      // dateTo: Moment(dateRange[0].endDate).format("YYYY-MM-DD"),
      smdId: smd?.id && smd.id !== "999" && smd.value !== "all" ? smd.id : "0",
      provinceId: prov?.id && prov.id !== "999" && prov.value !== "all" ? prov.id : "0",
      cityId: city?.id && city.id !== "999" && city.value !== "all" ? city.id : "0",
    };
  };

  useEffect(() => {
    fetchData();
  }, [timeRange]);
  const fetchData = async () => {
    const filters = getFilterParams();
    setLoading(true);
    try {
      const [resx, maps, geo, sales] = await Promise.all([
        getSummary( filters ),
        getMapDistribution( filters ),
        // getVisitTrends({ timeRange }),
        // getGeographicDistribution(),
        // getSalesPerformance(),
      ]);
      setSummary(resx);
      setMapDistribution(maps.stores);
      // setVisitTrends(trends);
      // setGeoDistribution(geo);
      // setSalesPerformance(sales);
    } catch (error) {
      console.error("Error fetching analytics data:", error);
    } finally {
      setLoading(false);
    }
  };
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "80vh" }}>
        <CSpinner color="primary" size="lg" />
      </div>
    );
  }

  return (
    <div className="store-analytics">
      <SalesPicker/>
      <CRow>
        <CCol xs={12} md={6} lg={3}>
          <CWidgetStatsA
            className="mb-4"
            color="primary"
            value={summary.data.summary.totalStores}
            title="Total Toko"
            icon={<CIcon icon={cibEventStore} height={36} />}
          />
        </CCol>
        <CCol xs={12} md={6} lg={3}>
          <CWidgetStatsA
            className="mb-4"
            color="info"
            value={summary.data.distribution.filter((s) => s.type == "A").map((s) => s.count) || 0}
            title="Toko Tipe A"
            icon={<CIcon icon={cilCalendar} height={36} />}
          />
        </CCol>
        <CCol xs={12} md={6} lg={3}>
          <CWidgetStatsA
            className="mb-4"
            color="warning"
            value={summary.data.summary.visitedStores}
            title="Toko Terkunjungi"
            icon={<CIcon icon={cilMap} height={36} />}
          />
        </CCol>
        <CCol xs={12} md={6} lg={3}>
          <CWidgetStatsA
            className="mb-4"
            color="danger"
            value={summary.data.summary.notVisitedStores}
            title="Toko Belum Terkunjungi"
            icon={<CIcon icon={cilChartPie} height={36} />}
          />
        </CCol>
      </CRow>

      <CRow>
        <CCol lg={6}>
          <CCard className="mb-4">
            <CCardHeader>
              <CRow>
                <CCol>Distribusi toko berdasarkan tipe</CCol>
              </CRow>
            </CCardHeader>
            <CCardBody>
              <CChartDoughnut
                data={{
                  labels: summary.data.distribution.map((item) => `Type ${item.type}`),
                  datasets: [
                    {
                      backgroundColor: ["#4BC0C0", "#36A2EB", "#FFCE56", "#FF6384"],
                      data: summary.data.distribution.map((item) => item.count),
                    },
                  ],
                }}
              />
            </CCardBody>
          </CCard>
        </CCol>

        <CCol lg={6}>
          <CCard className="mb-4">
            <CCardHeader>
              <CRow>
                <CCol>Distribusi toko berdasarkan visibility</CCol>
              </CRow>
            </CCardHeader>
            <CCardBody>
              <CChartDoughnut
                data={{
                  labels: summary.data.visibility.map((item) => ` ${item.visibility}`),
                  datasets: [
                    {
                      backgroundColor: ["#4BC0C0", "#36A2EB", "#FFCE56"],
                      data: summary.data.visibility.map((item) => item.store_count),
                    },
                  ],
                }}
              />
              <CFormLabel>*Berdasarkan data kunjungan terakhir</CFormLabel>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <CRow>
        <CCol lg={12}>
          <CCard className="mb-4">
            <CCardHeader>Sebaran Toko Berdasarkan Lokasi</CCardHeader>
            <CCardBody>
              <MapContainer center={center} zoom={13} style={{ height: "600px", width: "100%" }}>
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />

                {mapDistribution.map((store) => (
                  <Marker key={store.id} position={[parseFloat(store.latitude), parseFloat(store.longitude)]}>
                    <Popup>
                      <div style={{ cursor: "pointer" }} onClick={() => navigate(`/stores/details/${store.id}`)}>
                        <h3>{store.name}</h3>
                        <p>{store.address}</p>
                        <p style={{ color: "#0066cc", textDecoration: "underline", marginTop: "8px" }}>
                          Lihat Detail →
                        </p>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <CRow>
        <CCol lg={6}>
          <CCard className="mb-4">
            <CCardHeader>Geographic Distribution</CCardHeader>
            <CCardBody>
              <CChartBar
                data={{
                  labels: geoDistribution.map((item) => item.province),
                  datasets: [
                    {
                      label: "Stores per Province",
                      backgroundColor: "#f87979",
                      data: geoDistribution.map((item) => item.store_count),
                    },
                  ],
                }}
                options={{
                  indexAxis: "y",
                  responsive: true,
                  plugins: {
                    legend: {
                      position: "right",
                    },
                  },
                }}
              />
            </CCardBody>
          </CCard>
        </CCol>

        <CCol lg={6}>
          <CCard>
            <CCardHeader>Stores Needing Attention</CCardHeader>
            <CCardBody style={{ maxHeight: "400px", overflowY: "auto" }}>
              <CTable striped hover responsive>
                <CTableHead>
                  <CTableRow>
                    <CTableHeaderCell>Store</CTableHeaderCell>
                    <CTableHeaderCell>Type</CTableHeaderCell>
                    <CTableHeaderCell>Last Visit</CTableHeaderCell>
                    <CTableHeaderCell>Days Since Visit</CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {salesPerformance.map((store) => (
                    <CTableRow key={store.id}>
                      <CTableDataCell>{store.store_name}</CTableDataCell>
                      <CTableDataCell>
                        <CBadge color={store.type === "A" ? "primary" : "secondary"}>Type {store.type}</CBadge>
                      </CTableDataCell>
                      <CTableDataCell>
                        {store.last_visited ? new Date(store.last_visited).toLocaleDateString() : "Never"}
                      </CTableDataCell>
                      <CTableDataCell>
                        <CBadge color={store.days_since_last_visit > 30 ? "danger" : "warning"}>
                          {store.days_since_last_visit || "N/A"}
                        </CBadge>
                      </CTableDataCell>
                    </CTableRow>
                  ))}
                </CTableBody>
              </CTable>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    </div>
  );
};

export default StoreAnalytics;
